import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { RefreshActions } from '../../state/refresh'
import { useRequests } from '../../requests/useRequests'
import {
  validateUsername,
  validatePassword,
  validateMail,
  validatePasswords
} from '../../utils/validators'

const SparkleIcon = () => (
  <svg className='tw-w-5 tw-h-5 tw-mr-2 tw-text-pink-500' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={1.5} stroke='currentColor'>
    <path strokeLinecap='round' strokeLinejoin='round' d='M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L1.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L21 5.25l-.813 2.846a4.5 4.5 0 00-3.09 3.09L14.25 12l2.846.813a4.5 4.5 0 003.09 3.09L21 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09z' />
  </svg>
)

const EnvelopeIcon = () => (
  <svg className='tw-w-5 tw-h-5 tw-text-slate-400' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={1.5} stroke='currentColor'>
    <path strokeLinecap='round' strokeLinejoin='round' d='M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75' />
  </svg>
)

const RegistrationSection = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const requestMaker = useRequests()

  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [password, setPassword] = useState('')
  const [passwordAgain, setPasswordAgain] = useState('')
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const [emailError, setEmailError] = useState<string | null>(null)
  const [fullNameError, setFullNameError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordMatchError, setPasswordMatchError] = useState<string | null>(null)
  const [termsError, setTermsError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    setEmailError(null)
    setFullNameError(null)
    setPasswordError(null)
    setPasswordMatchError(null)
    setTermsError(null)

    let isValid = true

    if (!validateMail(email, setEmailError)) isValid = false
    if (!validateUsername(fullName, setFullNameError)) isValid = false
    if (!validatePassword(password, setPasswordError)) isValid = false
    if (!validatePasswords(password, passwordAgain, setPasswordMatchError)) isValid = false

    if (email.trim() === '') {
      setEmailError('Adres e-mail jest wymagany.')
      isValid = false
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError('Niepoprawny format adresu e-mail.')
      isValid = false
    }

    if (fullName.trim() === '') {
      setFullNameError('Imię i nazwisko jest wymagane.')
      isValid = false
    }
    if (password === '') {
      setPasswordError('Hasło jest wymagane.')
      isValid = false
    } else if (password.length < 6) {
      setPasswordError('Hasło musi mieć co najmniej 6 znaków.')
      isValid = false
    }
    if (password !== passwordAgain) {
      setPasswordMatchError('Hasła nie są takie same.')
      isValid = false
    }
    if (!termsAccepted) {
      setTermsError('Musisz zaakceptować warunki.')
      isValid = false
    }

    if (isValid) {
      try {
        await requestMaker.registerUser({
          username: fullName,
          password,
          email,
          phoneNumber: '',
          captchaToken: ''
        })
        dispatch(RefreshActions.setToastMessage({ message: 'Konto zostało utworzone' }))
        navigate('/login?type=new-account')
      } catch (e) {
        console.error('Błąd rejestracji', e)
        setFullNameError('Użytkownik z tymi danymi może już istnieć lub wystąpił inny błąd.')
      }
    }

    setIsLoading(false)
  }

  return (
    <section id='registration-section' className='tw-bg-slate-50 tw-py-10 md:tw-py-14 xl:tw-py-16'>
      <div className='tw-container tw-mx-auto tw-px-4'>
        <div className='tw-grid md:tw-grid-cols-12 md:tw-gap-x-12 lg:tw-gap-x-16 xl:tw-gap-x-24 tw-items-center'>
          <div className='md:tw-col-span-7 lg:tw-col-span-7'>
            <span className='tw-inline-block tw-bg-pink-100 tw-text-pink-700 tw-text-xs tw-font-semibold tw-px-3 tw-py-1 tw-rounded-full'>
              Wypróbuj za darmo przez 30 dni
            </span>
            <h3 className='tw-mt-5 tw-text-2xl md:tw-text-3xl xl:tw-text-4xl tw-leading-tight lg:tw-leading-tight xl:tw-leading-tight tw-font-bold tw-text-teal'>
              Zarejestruj się i skoncentruj się na tworzeniu,<br />a Kreatlo zajmie się resztą
            </h3>
            <p className='tw-mt-6 tw-text-base lg:tw-text-lg tw-text-slate-600 tw-leading-relaxed'>
              Zapomnij o przeszukiwaniu maili, gubieniu notatek i żmudnym zbieraniu informacji od Klientów. Nasze narzędzie to Twoje nowe centrum dowodzenia – pełna kontrola nad projektem w jednym, wygodnym narzędziu.
            </p>
          </div>

          <div className='md:tw-col-span-5 lg:tw-col-span-5 tw-mt-10 md:tw-mt-0'>
            <div className='tw-bg-white tw-p-6 sm:tw-p-8 tw-rounded-lg tw-shadow-lg'>
              <div className='tw-flex tw-items-center tw-mb-1'>
                <SparkleIcon />
                <h2 className='tw-text-lg tw-font-semibold tw-text-teal'>
                  Zarejestruj się za darmo!
                </h2>
              </div>
              <p className='tw-text-xs tw-text-slate-500 tw-mb-6 tw-ml-7'>
                Jesteś projektantem?{' '}
                <a href='#' className='tw-text-pink-600 hover:tw-underline tw-font-medium'>
                  Zarejestruj się tutaj
                </a>
              </p>

              <form onSubmit={handleSubmit} className='tw-space-y-5'>
                <div>
                  <label htmlFor='email' className='tw-sr-only'>Adres e-mail</label>
                  <div className='tw-relative'>
                    <div className='tw-absolute tw-inset-y-0 tw-left-0 tw-pl-3 tw-flex tw-items-center tw-pointer-events-none'>
                        <EnvelopeIcon />
                      </div>
                    <input
                        type='email'
                        name='email'
                        id='email'
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setEmailError(null) }}
                        placeholder='Adres e-mail'
                        className={`tw-w-full tw-pl-10 tw-pr-3 tw-py-3 tw-border ${emailError ? 'tw-border-red-500' : 'tw-border-slate-300'} tw-rounded-md focus:tw-ring-indigo-500 focus:tw-border-indigo-500 tw-text-sm`}
                      />
                  </div>
                  {emailError && <p className='tw-text-red-500 tw-text-xs tw-mt-1'>{emailError}</p>}
                </div>

                <div>
                  <label htmlFor='fullName' className='tw-sr-only'>Imię i nazwisko</label>
                  <input
                    type='text'
                    name='fullName'
                    id='fullName'
                    value={fullName}
                    onChange={(e) => { setFullName(e.target.value); setFullNameError(null) }}
                    placeholder='Nazwa użytkownika'
                    className={`tw-w-full tw-px-4 tw-py-3 tw-border ${fullNameError ? 'tw-border-red-500' : 'tw-border-slate-300'} tw-rounded-md focus:tw-ring-indigo-500 focus:tw-border-indigo-500 tw-text-sm`}
                  />
                  {fullNameError && <p className='tw-text-red-500 tw-text-xs tw-mt-1'>{fullNameError}</p>}
                </div>

                <div>
                  <label htmlFor='password' className='tw-sr-only'>Hasło</label>
                  <input
                    type='password'
                    name='password'
                    id='password'
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setPasswordError(null); setPasswordMatchError(null) }}
                    placeholder='Hasło'
                    className={`tw-w-full tw-px-4 tw-py-3 tw-border ${passwordError || passwordMatchError ? 'tw-border-red-500' : 'tw-border-slate-300'} tw-rounded-md focus:tw-ring-indigo-500 focus:tw-border-indigo-500 tw-text-sm`}
                  />
                  {passwordError && <p className='tw-text-red-500 tw-text-xs tw-mt-1'>{passwordError}</p>}
                </div>

                <div>
                  <label htmlFor='passwordAgain' className='tw-sr-only'>Potwierdź hasło</label>
                  <input
                    type='password'
                    name='passwordAgain'
                    id='passwordAgain'
                    value={passwordAgain}
                    onChange={(e) => { setPasswordAgain(e.target.value); setPasswordMatchError(null) }}
                    placeholder='Potwierdź hasło'
                    className={`tw-w-full tw-px-4 tw-py-3 tw-border ${passwordMatchError ? 'tw-border-red-500' : 'tw-border-slate-300'} tw-rounded-md focus:tw-ring-indigo-500 focus:tw-border-indigo-500 tw-text-sm`}
                  />
                  {passwordMatchError && <p className='tw-text-red-500 tw-text-xs tw-mt-1'>{passwordMatchError}</p>}
                </div>

                <div className='tw-flex tw-items-center tw-pt-2'>
                  <input
                    id='termsAccepted'
                    name='termsAccepted'
                    type='checkbox'
                    checked={termsAccepted}
                    onChange={(e) => { setTermsAccepted(e.target.checked); setTermsError(null) }}
                    className='tw-h-4 tw-w-4 tw-text-indigo-600 tw-border-slate-300 tw-rounded focus:tw-ring-indigo-500'
                  />
                  <label htmlFor='termsAccepted' className='tw-ml-2 tw-block tw-text-xs tw-text-slate-600'>
                    Zgadzam się z postanowieniami{' '}
                    <a href='#' className='tw-font-medium tw-text-pink-600 hover:tw-underline'>
                        Polityki Prywatności
                                        </a>{' '}
                    oraz{' '}
                    <a href='#' className='tw-font-medium tw-text-pink-600 hover:tw-underline'>
                        Regulaminu
                                        </a>.
                  </label>
                </div>
                {termsError && <p className='tw-text-red-500 tw-text-xs tw-mt-1'>{termsError}</p>}

                <button
                  type='submit'
                  disabled={isLoading}
                  className='tw-w-full tw-flex tw-justify-center tw-py-2 tw-px-4 tw-border tw-border-transparent tw-rounded-md tw-shadow-sm tw-text-sm tw-font-medium tw-text-slate-800 tw-bg-slate-200 hover:tw-bg-slate-300 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-slate-400 disabled:tw-opacity-50'
                >
                  {isLoading ? 'Tworzenie konta...' : 'ZAŁÓŻ KONTO'}
                </button>
              </form>

              <p className='tw-mt-6 tw-text-center tw-text-sm tw-text-slate-600'>
                Masz już konto?{' '}
                <a href='/login' className='tw-font-medium tw-text-pink-600 hover:tw-underline'>
                  Zaloguj się
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default RegistrationSection
